import { supabase } from '../lib/supabase';
import { logAction } from './logger';

export interface LevelDef {
  key: string;
  label: string;
  emoji: string;
  min_points: number;
}

export interface BadgeDef {
  key: string;
  label: string;
  emoji: string;
  description: string;
}

export interface PointsConfig {
  [actionKey: string]: number;
}

export interface GamificationConfig {
  levels: LevelDef[];
  points: PointsConfig;
  badges: BadgeDef[];
}

export interface UserPointsRow {
  user_id: string;
  points: number;
  level: string;
  total_checkins: number;
  total_matches_watched: number;
  badges: string[];
  updated_at: string;
  username?: string | null;
  email?: string | null;
}

export async function getConfig(): Promise<GamificationConfig | null> {
  const { data, error } = await supabase
    .from('gamification_config')
    .select('levels, points, badges')
    .eq('id', 1)
    .maybeSingle();
  if (error) {
    // Code 42P01 = "relation does not exist" (table absente) — on throw pour signaler la migration manquante
    if ((error as any).code === '42P01' || (error.message || '').includes('does not exist')) {
      throw error;
    }
    console.error(error);
    return null;
  }
  return data as GamificationConfig | null;
}

export async function saveConfig(patch: Partial<GamificationConfig>, adminEmail: string): Promise<void> {
  const { error } = await supabase
    .from('gamification_config')
    .update({ ...patch, updated_at: new Date().toISOString(), updated_by: adminEmail })
    .eq('id', 1);
  if (error) throw error;
  await logAction('update', 'gamification_config', '1', null, patch, 'Mise à jour config gamification');
}

export async function listUsersWithPoints(): Promise<UserPointsRow[]> {
  // 2 requêtes séparées pour éviter de bloquer si la FK profiles->user_points
  // n'est pas définie côté Supabase (le join nested-select peut échouer silencieusement)
  const [pointsRes, profilesRes] = await Promise.all([
    supabase.from('user_points')
      .select('user_id, points, level, total_checkins, total_matches_watched, badges, updated_at')
      .order('points', { ascending: false }),
    supabase.from('profiles').select('id, username, email'),
  ]);
  if (pointsRes.error) throw pointsRes.error;
  const profMap = new Map<string, { username: string | null; email: string | null }>();
  for (const p of (profilesRes.data || []) as any[]) {
    profMap.set(p.id, { username: p.username ?? null, email: p.email ?? null });
  }
  return (pointsRes.data || []).map((r: any) => ({
    user_id: r.user_id,
    points: r.points,
    level: r.level,
    total_checkins: r.total_checkins,
    total_matches_watched: r.total_matches_watched,
    badges: r.badges || [],
    updated_at: r.updated_at,
    username: profMap.get(r.user_id)?.username ?? null,
    email: profMap.get(r.user_id)?.email ?? null,
  }));
}

export async function adjustUserPoints(userId: string, delta: number, reason: string): Promise<void> {
  const { error } = await supabase.rpc('admin_adjust_points', {
    p_user_id: userId,
    p_delta: delta,
    p_reason: reason,
  });
  if (error) throw error;
}

export interface GamificationStats {
  totalUsers: number;
  totalPoints: number;
  byLevel: { level: string; count: number }[];
  topUsers: UserPointsRow[];
  badgesAwarded: { badge: string; count: number }[];
}

export async function getStats(allUsers: UserPointsRow[]): Promise<GamificationStats> {
  const totalUsers = allUsers.length;
  const totalPoints = allUsers.reduce((s, u) => s + (u.points || 0), 0);
  const byLevelMap: Record<string, number> = {};
  const badgeMap: Record<string, number> = {};
  for (const u of allUsers) {
    byLevelMap[u.level] = (byLevelMap[u.level] || 0) + 1;
    for (const b of u.badges || []) {
      badgeMap[b] = (badgeMap[b] || 0) + 1;
    }
  }
  const byLevel = Object.entries(byLevelMap).map(([level, count]) => ({ level, count }));
  const badgesAwarded = Object.entries(badgeMap).map(([badge, count]) => ({ badge, count }));
  const topUsers = [...allUsers].slice(0, 10);
  return { totalUsers, totalPoints, byLevel, topUsers, badgesAwarded };
}
